import React, { useContext, useEffect, useState } from "react";
import { UserContext } from "../../context/user.context";
import { UilSync } from "@iconscout/react-unicons";
import { ReactComponent as UAECurrencyWhite } from "../../images/uaeCurrencyWhite.svg";
import ZulalTopupCard from "./userComponents/ZulalTopUp1";
import logo from "../../images/zulal-logo/zulal-logo-lg.png";
import { fetchBalance, fetchWalletStatus } from "../../utils/SonicAPIV2";
import { createWalletTopUpOrder } from "../../utils/zulalWebAPI";
import toast from "react-hot-toast";
import EnableWalletRequest from "./EnableWalletRequest";

export const ZulalWallet = () => {
  const { currentUser } = useContext(UserContext);
  const [selectedValue, setSelectedValue] = useState(null);
  const [iCashBalance, setICashBalance] = useState(0.0);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isWalletStatusEnabled, setIsWalletStatusEnabled] = useState(false); // Assuming wallet is disabled by default

  useEffect(() => {
    checkWalletStatus();
  }, []);

  const checkWalletStatus = async () => {
    try {
      const status = await fetchWalletStatus();

      // console.log("Wallet status fetched:");
      // console.log("isWalletActive:", status.isWalletActive);

      setIsWalletStatusEnabled(status.isWalletActive);

      if (status.isWalletActive) {
        loadWalletBalance();
      }
    } catch (error) {
      // console.error("Error fetching wallet status:", error);
      // toast.error("Failed to check wallet status");
    }
  };

  const loadWalletBalance = async () => {
    try {
      setIsRefreshing(true);
      const customerICash = await fetchBalance();

      if (!customerICash || customerICash.balance === undefined) {
        // console.log("No iCash balance found");
        setICashBalance(0);
        return;
      }

      setICashBalance(customerICash.balance);
    } catch (error) {
      // console.error("Error fetching wallet balance:", error);
      toast.error("Failed to load wallet balance");
      setICashBalance(0);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedValue || !selectedValue.value) {
      toast.error("Please select a valid top-up amount");
      return;
    }

    // ✅ Show confirmation first
    const confirmed = window.confirm(
      `You are about to top up AED ${selectedValue.value}\n\nYou will be redirected to the payment gateway.`
    );
    if (!confirmed) return;

    // ✅ Start loading and processing
    setIsLoading(true);

    // ✅ Show processing toast
    const loadingToast = toast.loading("Creating payment order...");

    try {
      // console.log("=== Starting Top-Up Process ===");
      // console.log("Selected amount:", selectedValue.value);
      // console.log("Current balance:", iCashBalance);

      // ✅ Send request to backend
      const payload = {
        topUpValueName: selectedValue.name,
        topUpAmount: selectedValue.value,
        currentBalance: iCashBalance,
      };

      // console.log("Sending payload to backend:", payload);

      // ✅ Wait for the response from backend (this is where processing happens)
      const response = await createWalletTopUpOrder(payload);

      // console.log("=== Backend Response Received ===");
      // console.log("Response:", response);

      // ✅ Dismiss loading toast
      toast.dismiss(loadingToast);

      // ✅ Check if response is successful
      if (response.success && response.payByLinkUrl) {
        // console.log("✓ Payment link received:", response.payByLinkUrl);
        // console.log("✓ Order reference:", response.orderReference);

        // ✅ Show success message
        toast.success("Redirecting to payment gateway...", { duration: 2000 });

        // ✅ Wait a moment to show success message, then redirect
        setTimeout(() => {
          // console.log("=== Redirecting to Payment Gateway ===");
          window.location.href = response.payByLinkUrl;
        }, 1000);
      } else {
        // console.error("❌ Invalid response from backend:", response);
        toast.error(response.message || "Failed to create payment order");
        setIsLoading(false);
      }
    } catch (error) {
      // console.error("=== Error Creating Top-Up Order ===");
      // console.error("Error:", error);

      // ✅ Dismiss loading toast
      toast.dismiss(loadingToast);

      toast.error(
        error.message || "An error occurred while processing your request"
      );
      setIsLoading(false);
    }
  };

  const handleRefresh = async (e) => {
    e.preventDefault();
    await loadWalletBalance();
    toast.success("Wallet balance refreshed");
  };

  if (isWalletStatusEnabled === null) {
    return <div>Loading...</div>;
  }

  if (!isWalletStatusEnabled) {
    return <EnableWalletRequest />;
  }

  return (
    <div>
      <form onSubmit={handleSubmit}>
        <div className="mx-auto max-w-2xl mr-4 ml-4 mb-28">
          <div className="text-navarbarText text-3xl font-poppins font-medium mb-8">
            Zulal Wallet
            <p className="text-base text-textBlack font-poppins font-normal mt-2">
              Please select the amount you need to recharge.
              <br />
              <br />
              <span className="text-base font-semibold text-red-600">
                Note: Top-up amount is without TAX.
              </span>
            </p>
          </div>

          <div className="max-w-md">
            <ZulalTopupCard
              logo={logo}
              availableBalance={iCashBalance}
              onChange={(value) => {
                setSelectedValue(value);
                // console.log("Selected top-up value:", value);
              }}
            />
          </div>
        </div>

        <div className="mx-auto flex flex-row space-x-6">
          <button
            type="submit"
            disabled={!selectedValue || isLoading}
            className={`max-w-sm bg-secondary rounded-md ml-4 px-3.5 py-2.5 text-center text-sm font-semibold
                       text-textWhite shadow-sm
            ${
              !selectedValue || isLoading
                ? "cursor-not-allowed opacity-60"
                : "hover:bg-secondaryHover cursor-pointer"
            }`}
          >
            {isLoading ? (
              <span className="flex items-center justify-center gap-2">
                <svg
                  className="animate-spin h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Processing...
              </span>
            ) : selectedValue ? (
              <span className="flex items-center justify-center gap-2 text-base">
                Top Up Amount
                <span className="flex items-center justify-center gap-2 text-xl">
                  <UAECurrencyWhite className="w-4 h-4" />
                  {selectedValue.value}
                </span>
              </span>
            ) : (
              "Top Up Amount"
            )}
          </button>

          <button
            onClick={handleRefresh}
            type="button"
            disabled={isRefreshing}
            className="flex rounded-md bg-secondary hover:bg-secondaryHover h-12 w-12 items-center justify-center text-center disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <UilSync
              className={`w-6 text-textWhite ${
                isRefreshing ? "animate-spin" : ""
              }`}
            />
          </button>
        </div>
      </form>
    </div>
  );
};

export default ZulalWallet;
